import { useEffect, useRef, useState } from 'react';
import { Button } from '@heroui/react';

import type { Tab } from '../hooks/useSessions.tsx';

interface TabStripProps {
  tabs: Tab[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onClose: (id: string) => void;
  onRename: (id: string, title: string) => void;
  onNew: () => void;
}

/**
 * Browser-style tab strip. Each tab is a `<button>` for keyboard
 * activation, plus a small close `<button>` inside it (stop-propagation
 * on the close click so it doesn't also activate the tab). Double-click
 * on a chat tab title swaps it for an inline rename input. File tabs
 * cannot be renamed (their title is derived from the path).
 */
export function TabStrip({
  tabs,
  activeId,
  onSelect,
  onClose,
  onRename,
  onNew,
}: TabStripProps) {
  return (
    <div
      className="scroll-pane flex min-w-0 items-stretch gap-1 overflow-x-auto"
      role="tablist"
      aria-label="Open tabs"
    >
      {tabs.map((tab) => (
        <TabChip
          key={tab.id}
          tab={tab}
          isActive={tab.id === activeId}
          canClose={tabs.length > 1}
          onSelect={onSelect}
          onClose={onClose}
          onRename={onRename}
        />
      ))}
      <div className="flex items-center ps-1">
        <Button
          size="sm"
          variant="ghost"
          isIconOnly
          aria-label="New chat"
          onPress={onNew}
        >
          <span aria-hidden="true">＋</span>
        </Button>
      </div>
    </div>
  );
}

interface TabChipProps {
  tab: Tab;
  isActive: boolean;
  canClose: boolean;
  onSelect: (id: string) => void;
  onClose: (id: string) => void;
  onRename: (id: string, title: string) => void;
}

function TabChip({
  tab,
  isActive,
  canClose,
  onSelect,
  onClose,
  onRename,
}: TabChipProps) {
  const [isEditing, setEditing] = useState(false);
  const [draft, setDraft] = useState(tab.title);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const renamable = tab.kind === 'chat';

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing]);

  // If the title flips externally (e.g. server-pushed Haiku title), keep
  // the local draft in sync when not actively editing.
  useEffect(() => {
    if (!isEditing) setDraft(tab.title);
  }, [tab.title, isEditing]);

  function commit() {
    setEditing(false);
    if (draft.trim() && draft !== tab.title) {
      onRename(tab.id, draft.trim());
    } else {
      setDraft(tab.title);
    }
  }

  const tooltip =
    tab.kind === 'file'
      ? tab.scope === 'persistent'
        ? `/workspace/${tab.path}`
        : `/workspace/sessions/${tab.sessionId ?? '?'}/${tab.path}`
      : tab.title;

  return (
    <div
      role="tab"
      aria-selected={isActive}
      tabIndex={isActive ? 0 : -1}
      className={[
        'group flex items-center gap-2 rounded-t-md border border-b-0 px-3 py-1.5 text-sm transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/60',
        isActive
          ? 'border-white/12 bg-white/5'
          : 'border-transparent text-foreground/70 hover:bg-white/5',
      ].join(' ')}
      onClick={() => onSelect(tab.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(tab.id);
        }
      }}
      title={tooltip}
    >
      <span aria-hidden="true" className="opacity-70">
        {tab.kind === 'file' ? '📄' : '💬'}
      </span>
      {isEditing && renamable ? (
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
            } else if (e.key === 'Escape') {
              setDraft(tab.title);
              setEditing(false);
            }
          }}
          aria-label="Rename tab"
          spellCheck={false}
          className="w-32 rounded bg-transparent px-1 outline-none ring-1 ring-white/20 focus:ring-blue-400/60"
        />
      ) : (
        <span
          className="max-w-[14rem] truncate"
          onDoubleClick={(e) => {
            if (!renamable) return;
            e.stopPropagation();
            setDraft(tab.title);
            setEditing(true);
          }}
        >
          {tab.title}
        </span>
      )}
      {canClose && !isEditing && (
        <button
          type="button"
          aria-label={`Close ${tab.title}`}
          className="rounded p-0.5 text-xs opacity-0 transition group-hover:opacity-70 hover:bg-white/10 hover:opacity-100 focus:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/60"
          onClick={(e) => {
            e.stopPropagation();
            onClose(tab.id);
          }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          ✕
        </button>
      )}
    </div>
  );
}
